import {SelectValueInterface} from '@interfaces/select-value.interface';
import {DynamicFields} from '@namespaces/dynamic-fields.namespace';
import {FormGroup} from '@angular/forms';
import {RadioValueInterface} from '@components/form-field/form-radio-buttons/form-radio-buttons.component';
import {TechnicalDeviceNamespace} from '@namespaces/technical-device.namespace';
import {WorkingObjectNamespace} from '@namespaces/working-object.namespace';
import {UploadedFile} from '@interfaces/file.interface';

export namespace Conclusion {

    export interface FormData {
        id: number;
        attestation_document_id: number;
        conclusion_status_id: number;
        date: string;
        defects_total_length: number;
        equipment_ids: number[];
        number: string;
        reason: number;
        status_title: string;
        technical_card_id: number;
        technical_device_element_id: number;
        with_inspections: boolean;
        is_generate_conclusion: boolean;
    }

    export interface ViewData {
        id: number;
        number: string;
        date: string;
        status_title: string;
        reason_title: string;
        technical_card: TechnicalDeviceNamespace.ElementGroupTechcardInterface;
        technical_device: TechnicalDeviceNamespace.ApiDataInterface;
        technical_device_element: TechnicalDeviceNamespace.Element;
        working_object: WorkingObjectNamespace.Interface;
        scan: UploadedFile;
        ntd: {
            title: string;
            short_title: string;
            short_code: string;
        };
        with_inspections: boolean;
    }

    /**
     * Заключение для вывода в списке
     */
    export interface ListViewItem {
        id: number;
        is_blank: boolean;
        number: string;
        date: string;
        working_object: {
            title: string;
            org_customer: string;
        };
        ntd: {
            title: string;
            short_title: string;
            short_code: string;
        };
        control_order_number: string;
        technical_card_method_title: string;
        scan: UploadedFile;
        document: number;
        status_title: string;
        technical_card_id: number;
        technical_device_element_id: number;
        with_inspections: boolean;
    }

    /**
     * Значение испытания в форме.
     * ТО, ЧТО ПОСЫЛАЕМ В ФОРМЕ НА BACKEND
     */
    export interface InspectionFormValue {
        id: number;
        inspection_status_id: number;
        defects: Defect[];
        params: DynamicFields.FieldFormValue[];
    }

    export interface InspectionWithForm {
        id: number;
        defectParams: DynamicFields.Field[];
        defects: Defect[];
        fbGroup: FormGroup;

        /**
         * Все динамические параметры испытания
         */
        paramsWithForm: DynamicFields.ParamWithForm[];


        /**
         * Отображаемые в интерфейсе параметры испытания
         */
        showedParamsWithForm?: DynamicFields.ParamWithForm[];
    }

    /**
     * Репрезентер дефекта
     */
    export interface Defect {
        id: number;
        name: string;
        status: DefectStatus;
        params: DynamicFields.FieldFormValue[];
    }

    export enum DefectStatus {
        valid = 1,
        invalid,
    }

    export const DefectStatusesSelectValues: RadioValueInterface[] = [
        {value: DefectStatus.valid,    title: 'Годен'},
        {value: DefectStatus.invalid,  title: 'Не годен'}
    ];


    /**
     * Список подписантов с удостоверениями для выбора в селектах
     */
    export interface SignerSelectValue extends SelectValueInterface {
        certificates: SignerCertificate[];
    }

    export interface SignerCertificate extends SelectValueInterface {
        level: number;
    }

    /**
     * Структура подписанта, принятая от API сервера
     */
    export interface SignerStructure {
        /**
         * Тип подписанта (нужно для backend для понимания какой это именно подписант)
         */
        id: number;

        /**
         * Название поля подписанта для вывода в интерфейсе пользователю
         */
        label: string;

        /**
         * Требуемые уровни удостоверений для выбора
         */
        certificate_levels: number[];

        /**
         * Свободный ввод?
         */
        free_input?: boolean;
    }

    /**
     * Структура подписантов с Frontend-формой и помогающими полями
     */
    export interface SignerFormStructure extends SignerStructure {
        /**
         * Форма для данного подписанта
         */
        fbGroup: FormGroup;

        /**
         * Значения удостоверений текущего выбранного специалиста для подписанта
         */
        certificates_values: SelectValueInterface[];
    }


    /**
     * Значения формы подписантов, как они уходят в коллекции формы на backend
     */
    export interface SignerFormValue {
        conclusion_id: number;
        type_id: number;
        date: string;

        /**
         * SignerParam.free_input === false
         * Id внутреннего подписанта и его удостоверения
         */
        specialist_id: number;
        certificate_method_id: number;

        /**
         * SignerParam.free_input === true
         * Значения подлписанта и удостоверения, вписанные руками
         */
        outside_specialist: string;
        outside_certificate: string;
    }

    /**
     * Причины формирования заключения
     */
    export enum ConclusionReason {
        /**
         * Первичное заключение
         */
        primarily = 1,

        /**
         * После ремонта
         */
        afterRepair = 2,
    }

    /**
     * Значения причин заключения для выбора в селекте
     */
    export const ConclusionReasonSelectValues: SelectValueInterface[] = [
        {id: ConclusionReason.primarily, title: 'Первично'},
        {id: ConclusionReason.afterRepair, title: 'После ремонта'},
    ];

    /**
     * Данные по статусам испытаний и статусам заключения.
     * Включает в себя доступные для выбора статусы испытаний и общий статус всего заключения
     *
     * Пример:
     * inspections: {
     *     1: {
     *         select_values: [{id: 1, title: 'Годен'}, {id: 2, title: 'Не годен'}],
     *         value: 1
     *     },
     *     2: {
     *         select_values: [{id: 2, title: 'Не годен'}],
     *         value: 2
     *     },
     *     3: {
     *         select_values: [{id: 1, title: 'Годен'}, {id: 2, title: 'Не годен'}],
     *         value: 1
     *     }
     * },
     * conclusion: {
     *     select_values: [{id: 1, title: 'Годен'}, {id: 2, title: 'Не годен'}],
     *     value: 1
     * }
     */
    export interface StatusesApiData {
        /**
         * Статусы по испытаниям
         */
        inspections: {
            [inspection_id: string]: SelectValueInterface[]
        };
        /**
         * Статусы заключения
         */
        conclusion: SelectValueInterface[];
    }
}
